import type { CefrLevel } from '@/features/nectar/api/models'
import { Button, Select, SelectContent, SelectItem, SelectTrigger, SelectValue, Text } from '@/ui-kit'
import { ExternalLink } from 'lucide-react'
import { useState } from 'react'

const CEFR_LEVELS: { value: CefrLevel; label: string }[] = [
  { value: 'A1', label: 'A1 — Beginner' },
  { value: 'A2', label: 'A2 — Elementary' },
  { value: 'B1', label: 'B1 — Intermediate' },
  { value: 'B2', label: 'B2 — Upper Intermediate' },
  { value: 'C1', label: 'C1 — Advanced' },
  { value: 'C2', label: 'C2 — Proficient' }
]

const CEFR_GUIDE_URL = 'https://www.coe.int/en/web/common-european-framework-reference-languages/level-descriptions'

type PhaseSetupProps = {
  onStart: (selfLevel: CefrLevel) => void
  starting: boolean
}

export const PhaseSetup = ({ onStart, starting }: PhaseSetupProps) => {
  const [selfLevel, setSelfLevel] = useState<CefrLevel>('B1')

  return (
    <div
      className="mx-auto flex w-full max-w-[400px] flex-col items-center gap-6 rounded-2xl border border-solid border-gray-100 px-8 py-10"
      style={{ animation: 'nectar-slide-up 0.4s ease-out' }}
    >
      <div className="flex flex-col items-center gap-2 text-center">
        <Text type="h5">Proficiency Assessment</Text>
        <Text type="body2-regular" className="text-gray-80">
          This assessment determines your language level through two phases: a short adaptive text quiz followed by
          speech recordings.
        </Text>
      </div>

      <div className="flex w-full flex-col gap-2">
        <div className="flex items-center justify-between">
          <Text type="body2-regular" className="text-gray-90">
            Self-Assessed Level
          </Text>
          <a
            href={CEFR_GUIDE_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="text-gray-80 hover:text-gray-70 flex items-center gap-1 text-xs transition-colors"
          >
            What is CEFR? <ExternalLink className="size-3" />
          </a>
        </div>
        <Select value={selfLevel} onValueChange={(v) => setSelfLevel(v as CefrLevel)}>
          <SelectTrigger>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {CEFR_LEVELS.map((l) => (
              <SelectItem key={l.value} value={l.value}>
                {l.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <Button onClick={() => onStart(selfLevel)} isDisabled={starting} className="w-full">
        {starting ? 'Starting...' : 'Start Assessment'}
      </Button>
    </div>
  )
}
